// @ts-nocheck
/**
 * ドラフトシステム - ドラフト進行管理
 */

import {
  DRAFT_CONFIG,
  DRAFT_STATUS,
  DRAFT_DIRECTION,
  DRAFT_ECONOMY,
} from '../constants';
import { generateDraftPacks } from './draftPackGenerator';
import { getNPCsForPlayerCount, getNPCInfo } from '../data/npcPersonalities';
import { npcPickCard } from './npcPicker';

// ========================================
// セッション生成
// ========================================

/**
 * セッションIDを生成
 * @returns {string} セッションID
 */
const generateSessionId = () => {
  const now = new Date();
  const timestamp = now.toISOString().replace(/[-:T.Z]/g, '').slice(0, 14);
  const random = Math.random().toString(36).slice(2, 6);
  return `draft_${timestamp}_${random}`;
};

/**
 * プレイヤーリストを生成
 * @param {number} playerCount - 参加人数
 * @returns {Object[]} プレイヤーリスト
 */
const generatePlayers = (playerCount) => {
  const players = [
    {
      id: 'player',
      name: 'あなた',
      isHuman: true,
      seatIndex: 0,
      personality: null,
    },
  ];

  const npcPersonalities = getNPCsForPlayerCount(playerCount);

  npcPersonalities.forEach((personalityId, index) => {
    const npcInfo = getNPCInfo(personalityId);
    players.push({
      id: `npc${index + 1}`,
      name: npcInfo.name,
      isHuman: false,
      seatIndex: index + 1,
      personality: personalityId,
      avatar: npcInfo.avatar,
    });
  });

  return players;
};

/**
 * 新規ドラフトセッションを作成
 * @param {Object[]} allCards - 全カードデータ
 * @param {number} playerCount - 参加人数
 * @param {Map} [cardValueMap] - カード価値マップ
 * @returns {Object} ドラフトセッション状態
 */
export const createDraftSession = (allCards, playerCount = 4, cardValueMap = null) => {
  const packCount = DRAFT_CONFIG.PACKS_BY_PLAYER_COUNT[playerCount] || 4;
  const totalPacks = packCount * playerCount;

  // パックを生成
  const packs = generateDraftPacks(allCards, totalPacks, cardValueMap);

  // プレイヤーを生成
  const players = generatePlayers(playerCount);

  // 初期パックを配布
  const playerPacks = {};
  const playerPicks = {};

  players.forEach((player, index) => {
    playerPacks[player.id] = packs[index] || [];
    playerPicks[player.id] = [];
  });

  return {
    // セッション情報
    sessionId: generateSessionId(),
    status: DRAFT_STATUS.PICKING,
    createdAt: new Date().toISOString(),

    // 設定
    config: {
      playerCount,
      packSize: DRAFT_CONFIG.PACK_SIZE,
      packCount,
    },

    // 参加者
    players,

    // ドラフト進行
    draft: {
      currentPack: 1,
      currentRound: 1,
      direction: DRAFT_DIRECTION.CLOCKWISE,
      packs: playerPacks,
      picks: playerPicks,
      packHistory: [],
      allPacks: packs,
      packIndex: playerCount, // 次のパックのインデックス
    },

    // タイマー
    timer: {
      pickTimeLimit: DRAFT_CONFIG.PICK_TIME_LIMIT,
      currentPickTimer: DRAFT_CONFIG.PICK_TIME_LIMIT,
    },

    // デッキ構築
    deckBuilding: null,

    // トーナメント
    tournament: null,

    // 経済
    economy: {
      entryFee: DRAFT_ECONOMY.ENTRY_FEE,
      entryFeePaid: false,
    },
  };
};

// ========================================
// ドラフト進行
// ========================================

/**
 * 方向を取得
 * @param {number} packNumber - パック番号
 * @returns {string} 方向
 */
const getDirection = (packNumber) => {
  return packNumber % 2 === 1
    ? DRAFT_DIRECTION.CLOCKWISE
    : DRAFT_DIRECTION.COUNTERCLOCKWISE;
};

/**
 * 次のプレイヤーインデックスを取得
 * @param {number} currentIndex - 現在のインデックス
 * @param {number} playerCount - 参加人数
 * @param {string} direction - 方向
 * @returns {number} 次のプレイヤーインデックス
 */
const getNextPlayerIndex = (currentIndex, playerCount, direction) => {
  if (direction === DRAFT_DIRECTION.CLOCKWISE) {
    return (currentIndex + 1) % playerCount;
  } else {
    return (currentIndex - 1 + playerCount) % playerCount;
  }
};

/**
 * プレイヤーがカードをピック
 * @param {Object} session - セッション状態
 * @param {string} playerId - プレイヤーID
 * @param {string} cardId - ピックするカードID
 * @returns {Object} 更新されたセッション状態
 */
export const playerPickCard = (session, playerId, cardId) => {
  const newSession = JSON.parse(JSON.stringify(session));
  const { packs, picks } = newSession.draft;

  // プレイヤーのパックからカードを取得
  const playerPack = packs[playerId];
  const cardIndex = playerPack.findIndex(c => c.id === cardId);

  if (cardIndex === -1) {
    console.error('[DraftManager] Card not found in pack:', cardId);
    return session;
  }

  // カードをピック
  const pickedCard = playerPack.splice(cardIndex, 1)[0];
  picks[playerId].push(pickedCard);

  return newSession;
};

/**
 * NPCが全員ピック
 * @param {Object} session - セッション状態
 * @returns {Object} 更新されたセッション状態
 */
export const npcPickAll = (session) => {
  const newSession = JSON.parse(JSON.stringify(session));
  const { packs, picks } = newSession.draft;
  const { players } = newSession;

  // 各NPCがピック
  players.forEach(player => {
    if (player.isHuman) return;

    const playerPack = packs[player.id];
    if (!playerPack || playerPack.length === 0) return;

    // 他プレイヤーのピックを収集（ヘイトドラフト用）
    const otherPicks = players
      .filter(p => p.id !== player.id)
      .map(p => picks[p.id] || []);

    // NPCがピック
    const pickedCard = npcPickCard(
      playerPack,
      player.personality,
      picks[player.id],
      otherPicks
    );

    if (pickedCard) {
      const cardIndex = playerPack.findIndex(c => c.id === pickedCard.id);
      if (cardIndex !== -1) {
        playerPack.splice(cardIndex, 1);
        picks[player.id].push(pickedCard);
      }
    }
  });

  return newSession;
};

/**
 * パックを回す
 * @param {Object} session - セッション状態
 * @returns {Object} 更新されたセッション状態
 */
export const rotatePacks = (session) => {
  const newSession = JSON.parse(JSON.stringify(session));
  const { draft, players } = newSession;
  const { packs, direction } = draft;
  const playerCount = players.length;

  // 一時保存
  const tempPacks = {};
  players.forEach(player => {
    tempPacks[player.id] = packs[player.id];
  });

  // パックを回す
  players.forEach(player => {
    const currentIndex = player.seatIndex;
    const nextIndex = getNextPlayerIndex(currentIndex, playerCount, direction);
    const nextPlayer = players.find(p => p.seatIndex === nextIndex);
    packs[nextPlayer.id] = tempPacks[player.id];
  });

  return newSession;
};

/**
 * ラウンドを進める
 * @param {Object} session - セッション状態
 * @returns {Object} 更新されたセッション状態
 */
export const advanceRound = (session) => {
  const newSession = JSON.parse(JSON.stringify(session));
  const { draft, config, players } = newSession;

  draft.currentRound++;

  // パックが空になったかチェック
  const anyPackEmpty = players.some(p =>
    !draft.packs[p.id] || draft.packs[p.id].length === 0
  );

  if (anyPackEmpty) {
    // 次のパックへ
    draft.currentPack++;
    draft.currentRound = 1;

    if (draft.currentPack > config.packCount) {
      // ドラフト終了 → デッキ構築へ
      newSession.status = DRAFT_STATUS.BUILDING;
      newSession.deckBuilding = initializeDeckBuilding(newSession);
    } else {
      // 方向を切り替え
      draft.direction = getDirection(draft.currentPack);

      // 新しいパックを配布
      const startIndex = draft.packIndex;
      players.forEach((player, index) => {
        draft.packs[player.id] = draft.allPacks[startIndex + index] || [];
      });
      draft.packIndex += players.length;
    }
  }

  return newSession;
};

/**
 * 1ラウンド分のピック処理を実行
 * @param {Object} session - セッション状態
 * @param {string} playerCardId - プレイヤーがピックするカードID
 * @returns {Object} 更新されたセッション状態
 */
export const executePickRound = (session, playerCardId) => {
  let newSession = session;

  // 1. プレイヤーがピック
  newSession = playerPickCard(newSession, 'player', playerCardId);

  // 2. NPCが全員ピック
  newSession = npcPickAll(newSession);

  // 3. パックを回す
  newSession = rotatePacks(newSession);

  // 4. ラウンドを進める
  newSession = advanceRound(newSession);

  return newSession;
};

// ========================================
// デッキ構築
// ========================================

/**
 * デッキ構築状態を初期化
 * @param {Object} session - セッション状態
 * @returns {Object} デッキ構築状態
 */
const initializeDeckBuilding = (session) => {
  const { players, draft } = session;
  const deckBuilding = {};

  players.forEach(player => {
    const picks = draft.picks[player.id] || [];

    if (player.isHuman) {
      deckBuilding[player.id] = {
        deck: [],
        sideboard: [...picks],
        isConfirmed: false,
      };
    } else {
      // NPCは自動でデッキ構築
      const { deck, sideboard } = autoConstructDeck(picks);
      deckBuilding[player.id] = {
        deck,
        sideboard,
        isConfirmed: true,
      };
    }
  });

  return deckBuilding;
};

/**
 * 自動デッキ構築
 * @param {Object[]} picks - ピック済みカード
 * @returns {Object} { deck, sideboard }
 */
const autoConstructDeck = (picks) => {
  // カードをスコア順でソート（シンプルな実装）
  const scored = picks.map(card => ({
    card,
    score: getCardScore(card),
  }));
  scored.sort((a, b) => b.score - a.score);

  // 上位35枚をデッキに
  const deckSize = Math.min(DRAFT_CONFIG.RECOMMENDED_DECK_SIZE, picks.length);
  const deck = scored.slice(0, deckSize).map(s => s.card);
  const sideboard = scored.slice(deckSize).map(s => s.card);

  return { deck, sideboard };
};

/**
 * カードスコアを計算（自動構築用）
 * @param {Object} card - カード
 * @returns {number} スコア
 */
const getCardScore = (card) => {
  let score = 0;

  // レアリティ
  const rarityScores = { C: 10, UC: 20, R: 40, SR: 70, UR: 100, HR: 150 };
  score += rarityScores[card.rarity] || 10;

  // モンスターを優先
  if (card.type === 'monster') score += 30;

  // コスト（低〜中コストを優先）
  const cost = card.cost || 0;
  if (cost <= 2) score += 20;
  else if (cost <= 4) score += 15;
  else score += 5;

  return score;
};

// ========================================
// ユーティリティ
// ========================================

/**
 * 進捗率を計算
 * @param {Object} session - セッション状態
 * @returns {string} 進捗率
 */
export const calculateProgress = (session) => {
  const { config, draft, status } = session;

  if (status === DRAFT_STATUS.FINISHED) return '100%';
  if (status === DRAFT_STATUS.TOURNAMENT) return '90%';
  if (status === DRAFT_STATUS.BUILDING) return '75%';

  // ピック中の進捗
  const totalRounds = config.packSize * config.packCount;
  const completedRounds =
    (draft.currentPack - 1) * config.packSize + (draft.currentRound - 1);
  const progress = Math.floor((completedRounds / totalRounds) * 75);

  return `${progress}%`;
};

/**
 * サマリーを生成
 * @param {Object} session - セッション状態
 * @returns {Object} サマリー
 */
export const generateSummary = (session) => {
  const { status, config, draft, players } = session;
  const playerPicks = draft.picks['player'] || [];

  return {
    status,
    playerCount: config.playerCount,
    currentPack: draft.currentPack,
    currentRound: draft.currentRound,
    totalPicks: playerPicks.length,
    progress: calculateProgress(session),
    lastAction: `パック${draft.currentPack} ラウンド${draft.currentRound}`,
  };
};

export default {
  createDraftSession,
  playerPickCard,
  npcPickAll,
  rotatePacks,
  advanceRound,
  executePickRound,
  calculateProgress,
  generateSummary,
};
